import type { FormikProps } from 'formik'
import type { FunctionComponent } from 'react'

import {
	CurrencyInput,
	FileUpload,
	FormItem,
	MultipleChoice,
	NumberInput,
	TextInput,
} from '@monthio/design-system'
import { useIntlConfig } from '@monthio/react-components'
import {
	InputGroup,
	InputSection,
} from '#modules/Questionnaire/Components/shared'
import { getIn, useField } from 'formik'
import { useTranslation } from 'react-i18next'

import type { QuestionDefinition } from './types'

import { ScrollToViewContainer } from '../../modulesTemp/shared/ScrollToViewContainer'
import { arraysAreEqual } from '../IncomeVerification/Components/arraysAreEqual'
import { activeSubquestions } from './model/questionnaireConfig/utils'
import { SliderQuestion } from './SliderQuestion'
import { getValidationConstraintValue, isQuestionRequired } from './types'

const allowedFileTypes = [
	{ mime: 'image/jpeg', type: 'jpeg' },
	{ mime: 'image/jpg', type: 'jpg' },
	{ mime: 'image/jfif', type: 'jfif' },
	{ mime: 'image/png', type: 'png' },
	{ mime: 'image/bmp', type: 'bmp' },
	{ mime: 'image/webp', type: 'webp' },
	{ mime: 'application/pdf', type: 'pdf' },
]

interface QuestionComponentProps {
	question: QuestionDefinition
	formProps: FormikProps<any>
	formikFieldPrefix?: string
}

interface QuestionRendererProps {
	question: QuestionDefinition
	fieldName: string
	formProps: FormikProps<any>
	formikFieldPrefix: string
}

const MultiSelectQuestion: FunctionComponent<QuestionRendererProps> = ({
	question,
	fieldName,
}) => {
	const { t } = useTranslation()
	const [field, _meta, { setTouched, setValue }] = useField(fieldName)

	return (
		<MultipleChoice
			type="checkbox"
			fieldName={fieldName}
			selected={field.value}
			onChange={(value) => {
				setValue(value)
			}}
			onBlur={() => setTouched(true)}
			choices={
				question.options?.map((x) => ({
					label: t(x.label),
					value: x.value?.toString(),
				})) ?? []
			}
		/>
	)
}

const SingleSelectQuestion: FunctionComponent<QuestionRendererProps> = ({
	question,
	fieldName,
}) => {
	const { t } = useTranslation()
	const [field, _meta, { setTouched, setValue }] = useField(fieldName)

	return (
		<MultipleChoice
			type="radio"
			fieldName={fieldName}
			selected={field.value}
			onChange={(value) => {
				setValue(value)
			}}
			onBlur={() => setTouched(true)}
			choices={
				question.options?.map((x) => ({
					label: t(x.label),
					value: x.value?.toString(),
				})) ?? []
			}
		/>
	)
}

const TextInputQuestion: FunctionComponent<QuestionRendererProps> = ({
	fieldName,
	formProps,
}) => {
	const [_field, _meta, { setTouched, setValue }] = useField(fieldName)
	return (
		<TextInput
			onChange={(e) => setValue(e.target.value)}
			maxLength={100}
			hasError={
				_meta.touched ? !!getIn(formProps.errors, fieldName) : undefined
			}
			onBlur={() => {
				setTouched(true)
			}}
		/>
	)
}

const NumberInputQuestion: FunctionComponent<QuestionRendererProps> = ({
	question,
	fieldName,
	formProps,
}) => {
	const min = question.validations?.min
		? getValidationConstraintValue(question.validations.min)
		: undefined
	const max = question.validations?.max
		? getValidationConstraintValue(question.validations.max)
		: undefined

	return (
		<NumberInput
			value={getIn(formProps.values, fieldName) ?? ''}
			onChange={(value) => formProps.setFieldValue(fieldName, value)}
			min={min}
			max={max}
			step={question.step}
			onError={getIn(formProps.errors, fieldName)}
		/>
	)
}

const CurrencyInputQuestion: FunctionComponent<QuestionRendererProps> = ({
	question,
	fieldName,
	formProps,
}) => {
	const intlConfig = useIntlConfig({ showCurrency: true })

	return (
		<CurrencyInput
			value={getIn(formProps.values, fieldName) ?? ''}
			onChange={(value) => {
				formProps.setFieldValue(fieldName, value)
			}}
			currency={intlConfig.currency ?? 'DKK'}
			locale={intlConfig.locale}
			step={question.step}
			onError={getIn(formProps.errors, fieldName)}
			allowNegativeValues={false}
		/>
	)
}

const DocumentUploadQuestion: FunctionComponent<QuestionRendererProps> = ({
	question,
	fieldName,
}) => {
	const { t } = useTranslation()
	const [field, _meta, { setTouched, setValue }] = useField<File[]>(fieldName)
	const files = Array.isArray(field.value) ? field.value : []
	const title = question.label

	const translations = {
		addFileButtonAriaLabel: t('add-file'),
		removeFileButtonAriaLabel: (file: File) =>
			t('remove-named-file', { fileName: file.name }),
	}

	return (
		<FileUpload
			title={title}
			onBlur={() => {
				setTouched(true)
			}}
			translations={translations}
			acceptedFileTypes={allowedFileTypes}
			value={files}
			onChange={(newFiles) => {
				if (!arraysAreEqual(newFiles, files)) {
					setValue([...newFiles])
				}
			}}
		/>
	)
}

const Subquestion: FunctionComponent<{
	question: QuestionDefinition
	formProps: FormikProps<any>
	formikFieldPrefix: string
}> = ({ question, formProps, formikFieldPrefix }) => {
	const fullFieldName = formikFieldPrefix
		? `${formikFieldPrefix}.${question.formFieldKey}`
		: question.formFieldKey

	const subQuestions = activeSubquestions(
		formProps.values,
		question,
		fullFieldName,
	)

	if (!subQuestions?.length) {
		return null
	}

	return (
		<InputSection>
			{subQuestions.map((subquestion) => (
				<QuestionComponentInner
					key={subquestion.id}
					question={subquestion}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			))}
		</InputSection>
	)
}

const QuestionRenderer: FunctionComponent<QuestionRendererProps> = ({
	question,
	fieldName,
	formProps,
	formikFieldPrefix,
}) => {
	switch (question.questionType) {
		case 'slider':
			return (
				<SliderQuestion
					fieldName={fieldName}
					formProps={formProps}
					{...question.sliderConfig}
				/>
			)
		case 'singleSelect':
			return (
				<SingleSelectQuestion
					question={question}
					fieldName={fieldName}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			)
		case 'multiSelect':
			return (
				<MultiSelectQuestion
					question={question}
					fieldName={fieldName}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			)
		case 'textInput':
			return (
				<TextInputQuestion
					question={question}
					fieldName={fieldName}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			)
		case 'numberInput':
			return (
				<NumberInputQuestion
					question={question}
					fieldName={fieldName}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			)
		case 'currencyInput':
			return (
				<CurrencyInputQuestion
					question={question}
					fieldName={fieldName}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			)
		case 'documentUpload':
			return (
				<DocumentUploadQuestion
					question={question}
					fieldName={fieldName}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			)
		default:
			return null
	}
}

const QuestionComponentInner: FunctionComponent<QuestionComponentProps> = ({
	question,
	formProps,
	formikFieldPrefix = '',
}: QuestionComponentProps) => {
	const { t } = useTranslation()

	const fieldName = formikFieldPrefix
		? `${formikFieldPrefix}.${question.formFieldKey}`
		: question.formFieldKey
	const [_field, meta] = useField(fieldName)

	return (
		<>
			{/**
			 * Have to do a bit of double work here, duplicating the 'formikIntegration' components somewhat. Reason is the backwards
			 * compatability with the existing design. Once the design-system has been fully rolled out, we can use the formik integration components here.
			 */}
			<FormItem
				label={question.label}
				isRequired={isQuestionRequired(question)}
				requiredText={t('required')}
				errorText={meta.touched ? meta.error : undefined}
				hintText={question.helpText}
			>
				<QuestionRenderer
					question={question}
					fieldName={fieldName}
					formProps={formProps}
					formikFieldPrefix={formikFieldPrefix}
				/>
			</FormItem>

			<Subquestion
				question={question}
				formProps={formProps}
				formikFieldPrefix={formikFieldPrefix}
			/>
		</>
	)
}

export const QuestionComponent: FunctionComponent<QuestionComponentProps> = (
	props,
) => {
	return (
		<ScrollToViewContainer>
			<InputGroup>
				<QuestionComponentInner {...props} />
			</InputGroup>
		</ScrollToViewContainer>
	)
}
